import {
  ActionIcon,
  Badge,
  Button,
  FileButton,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Modal,
  rem,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconCloudUpload,
  IconEdit,
  IconFileExport,
  IconFileImport,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { Editor } from "@tinymce/tinymce-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { repositoryDelivery } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { paginationBase } from "../../../_base/model/_base/BaseTable";
import { BASE_API_DELIVERY } from "../../../config";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";

type TypePromotionItem = {
  itemId: number;
  itemCode: string;
  itemName: string;
  maLinhVuc: string;
  tenLinhVuc: string;
  maNganh: string;
  tenNganh: string;
  maLoai: string;
  tenLoai: string;
  maChung: string;
  tenChung: string;
  maNhom: string;
  tenNhom: string;
  maModel: string | null;
  tenModel: string | null;
  maHangSx: string | null;
  tenHangSx: string | null;
  pl1: string | null;
  tenPl1: string | null;
  partNumber: string | null;
  tenP12: string | null;
  pl3: string | null;
  tenPl3: string | null;
  pl4: string | null;
  pl5: string | null;
  pl6: string | null;
  tenPl4: string | null;
  tenPl5: string | null;
  tenPl6: string | null;
  attribute51: number | null;
  attribute52: number | null;
  attribute53: number | null;
  attribute54: number | null;
  attribute3: string | null;
  attribute65: boolean | null;
  lastUpdateBy: string | null;
  lastUpdateDate: string | null;
  isBuildPc: string;
  warranty: number | null;
  warrantyVendor: number | null;
  moTaKm: string | null;
  duyetMoTaKm: string | null; // "1" = đã duyệt, "0" = chưa duyệt
  promotionDescriptionStatus: boolean | null; // true = đã duyệt, false = chưa duyệt
};

const DetailPromotion = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<TypePromotionItem[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState(paginationBase);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TypePromotionItem | null>(
    null
  );
  const [promotionDescription, setPromotionDescription] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Batch update states
  const [batchUpdateModalOpen, setBatchUpdateModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [batchUpdateStatus, setBatchUpdateStatus] = useState<boolean>(true);
  const [isUpdatingBatch, setIsUpdatingBatch] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const resetRef = useRef<() => void>(null);

  // Kiểm tra content TinyMCE có rỗng không
  const isContentEmpty = (content: string) => {
    if (!content || content.trim() === "") return true;
    // Remove HTML tags and check if there's actual content
    const textContent = content.replace(/<[^>]*>/g, "").trim();
    return textContent === "";
  };
  const [search, setSearch] = useDebouncedState(
    {
      maLinhVuc: "",
      maNganh: "",
      maLoai: "",
      maChung: "",
      maNhom: "",
      maModel: "",
      maHangSx: "",
      keySearch: "",
      hasPromotionDescription: "", // Thêm filter cho HasPromotionDescription
      promotionDescriptionStatus: "", // Thêm filter cho PromotionDescriptionStatus (trạng thái duyệt)
    },
    300
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

    // Thêm các tham số mới cho promotion
    url += `&IsPromotion=true`;

    // Thêm filter cho HasPromotionDescription nếu được chọn
    if (search.hasPromotionDescription !== "") {
      url += `&HasPromotionDescription=${search.hasPromotionDescription}`;
    }

    // Thêm filter cho PromotionDescriptionStatus nếu được chọn
    if (search.promotionDescriptionStatus !== "") {
      url += `&PromotionDescriptionStatus=${search.promotionDescriptionStatus}`;
    }

    if (search.maLinhVuc.length > 0) {
      url += `&MaLinhVuc=${search.maLinhVuc}`;
    }

    if (search.maNganh.length > 0) {
      url += `&MaNganh=${search.maNganh}`;
    }

    if (search.maLoai.length > 0) {
      url += `&MaLoai=${search.maLoai}`;
    }

    if (search.maChung.length > 0) {
      url += `&MaChung=${search.maChung}`;
    }

    if (search.maNhom.length > 0) {
      url += `&MaNhom=${search.maNhom}`;
    }

    if (search.maModel.length > 0) {
      url += `&MaModel=${search.maModel}`;
    }

    if (search.maHangSx.length > 0) {
      url += `&MaHangSx=${search.maHangSx}`;
    }

    if (search.keySearch.length > 0) {
      url += `&KeySearch=${search.keySearch}`;
    }

    try {
      const response = await repositoryDelivery.get(
        `/api/v1/TblDmItemCategoryCombination/get-list-for-promotion${url}`
      );
      if (response && response.httpStatusCode === 200) {
        let result = response.data;
        setIsLoading(false);
        setData(result);
        setRowCount(response.totalCount);
        // table.resetRowSelection(); // Will be called after table is created
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      NotificationExtension.Fails("Lỗi khi tải dữ liệu sản phẩm khuyến mãi");
    } finally {
      setIsRefetching(false);
    }
  }, [pagination, search]);

  const exportDataExcel = async () => {
    open();
    let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

    url += `&IsPromotion=true`;

    // Thêm filter cho HasPromotionDescription nếu được chọn
    if (search.hasPromotionDescription !== "") {
      url += `&HasPromotionDescription=${search.hasPromotionDescription}`;
    }

    // Thêm filter cho PromotionDescriptionStatus nếu được chọn
    if (search.promotionDescriptionStatus !== "") {
      url += `&PromotionDescriptionStatus=${search.promotionDescriptionStatus}`;
    }

    if (search.maLinhVuc.length > 0) {
      url += `&MaLinhVuc=${search.maLinhVuc}`;
    }

    if (search.maNganh.length > 0) {
      url += `&MaNganh=${search.maNganh}`;
    }

    if (search.maLoai.length > 0) {
      url += `&MaLoai=${search.maLoai}`;
    }

    if (search.maChung.length > 0) {
      url += `&MaChung=${search.maChung}`;
    }

    if (search.maNhom.length > 0) {
      url += `&MaNhom=${search.maNhom}`;
    }

    if (search.maModel.length > 0) {
      url += `&MaModel=${search.maModel}`;
    }

    if (search.maHangSx.length > 0) {
      url += `&MaHangSx=${search.maHangSx}`;
    }

    if (search.keySearch.length > 0) {
      url += `&KeySearch=${search.keySearch}`;
    }

    try {
      const baseUrl = `/api/v1/ExportExcel/export-promotion-items${url}`;
      const _url = BASE_API_DELIVERY + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(/"/g, "")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const urlss = window.URL.createObjectURL(blob);
      const _name = new Date().toISOString().slice(0, 10);
      let filename = `danh_sach_san_pham_khuyen_mai_${_name}.xlsx`;
      const link = document.createElement("a");
      link.href = urlss;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlss);
      NotificationExtension.Success("Xuất Excel thành công!");
    } catch (error) {
      console.error("Export error:", error);
      NotificationExtension.Fails("Lỗi khi xuất Excel");
    } finally {
      close();
    }
  };

  const uploadFile = async (e: File) => {
    if (!e) {
      NotificationExtension.Fails("File rỗng hoặc chưa chọn file !");
      return;
    }
    open();
    // await new Promise((resolve) => setTimeout(resolve, 2000));
    const formData = new FormData();
    formData.append("file", e);

    try {
      const resSaveFile = await repositoryDelivery.post<MessageResponse<any>>(
        "/api/v1/TblDmItemCategoryCombination/import-mo-ta-km",
        formData
      );
      if (resSaveFile && resSaveFile.success) {
        resetRef.current?.();
        NotificationExtension.Success("Import file thành công !");
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        NotificationExtension.Fails("Import file thất bại !");
        resetRef.current?.();
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
      resetRef.current?.();
    }
  };

  const handleChangeSearchValue = (value: string, key: string) => {
    if (value !== "Invalid date")
      setSearch((prevData) => ({ ...prevData, [key]: value }));
    else {
      setSearch((prevData) => ({ ...prevData, [key]: "" }));
    }
  };

  // Xử lý mở modal chỉnh sửa mô tả
  const handleEditDescription = useCallback((item: TypePromotionItem) => {
    setEditingItem(item);
    const content = item.moTaKm || "";
    setPromotionDescription(content);
    setEditModalOpen(true);
  }, []);

  // Xử lý cập nhật mô tả khuyến mãi
  const handleUpdateDescription = async () => {
    if (!editingItem) return;

    setIsUpdating(true);
    try {
      const response = await repositoryDelivery.post(
        `/api/v1/TblDmItemCategoryCombination/update-promotion-description`,
        {
          id: editingItem.itemId,
          promotionDescription: promotionDescription.trim(),
        }
      );

      if (response && response.httpStatusCode === 200) {
        NotificationExtension.Success("Cập nhật mô tả khuyến mãi thành công!");
        setEditModalOpen(false);
        setEditingItem(null);
        setPromotionDescription("");
        // Refresh data
        fetchData();
      } else {
        NotificationExtension.Fails("Cập nhật mô tả khuyến mãi thất bại!");
      }
    } catch (error) {
      console.error("Update error:", error);
      NotificationExtension.Fails("Lỗi khi cập nhật mô tả khuyến mãi");
    } finally {
      setIsUpdating(false);
    }
  };

  // Xử lý đóng modal
  const handleCloseModal = () => {
    setEditModalOpen(false);
    setEditingItem(null);
    setPromotionDescription("");
  };

  // Xử lý batch update trạng thái duyệt
  const handleBatchUpdateStatus = async () => {
    const selectedRows = Object.keys(rowSelection);
    if (selectedRows.length === 0) {
      NotificationExtension.Warn("Vui lòng chọn ít nhất một sản phẩm!");
      return;
    }

    setIsUpdatingBatch(true);
    try {
      const selectedIds = selectedRows.map(
        (index) => data[parseInt(index)].itemId
      );

      const response = await repositoryDelivery.post(
        "/api/v1/TblDmItemCategoryCombination/update-promotion-description-status",
        {
          ids: selectedIds,
          promotionDescriptionStatus: batchUpdateStatus,
        }
      );

      if (response && response.success) {
        NotificationExtension.Success(
          `Cập nhật trạng thái duyệt thành công cho ${selectedIds.length} sản phẩm!`
        );
        setBatchUpdateModalOpen(false);
        setRowSelection({});
        fetchData(); // Refresh data
      } else {
        NotificationExtension.Fails(
          response?.message || "Cập nhật trạng thái duyệt thất bại!"
        );
      }
    } catch (error) {
      console.error("Batch update error:", error);
      NotificationExtension.Fails("Lỗi khi cập nhật trạng thái duyệt");
    } finally {
      setIsUpdatingBatch(false);
    }
  };

  // Xử lý sync sản phẩm lên web
  const handleSyncToWeb = async () => {
    const selectedRows = Object.keys(rowSelection);
    if (selectedRows.length === 0) {
      NotificationExtension.Warn("Vui lòng chọn ít nhất một sản phẩm!");
      return;
    }

    setIsSyncing(true);
    try {
      const selectedItemCodes = selectedRows.map(
        (index) => data[parseInt(index)].itemCode
      );

      const response = await repositoryDelivery.post(
        "/api/v1/TblDmItemCategoryCombination/sync-promotion-description-status-to-web",
        {
          itemCodes: selectedItemCodes,
          batchSize: 100,
        }
      );

      if (response && response.success) {
        NotificationExtension.Success(
          `Đồng bộ thành công ${selectedItemCodes.length} sản phẩm lên web!`
        );
        setSyncModalOpen(false);
        setRowSelection({});
      } else {
        NotificationExtension.Fails(
          response?.message || "Đồng bộ sản phẩm lên web thất bại!"
        );
      }
    } catch (error) {
      console.error("Sync error:", error);
      NotificationExtension.Fails("Lỗi khi đồng bộ sản phẩm lên web");
    } finally {
      setIsSyncing(false);
    }
  };

  const columns = React.useMemo<MRT_ColumnDef<TypePromotionItem>[]>(
    () => [
      {
        header: "STT",
        size: 30,
        Cell: ({ row }) => (
          <>
            <span
              style={{
                fontWeight: "bold",
              }}
            >
              {row.index + 1}
            </span>
          </>
        ),
        enableResizing: false,
      },
      {
        accessorKey: "itemCode",
        header: "Mã Sản Phẩm",
        Cell: ({ renderedCellValue }) => (
          <>
            <Badge variant="dot" color="green" radius="xs">
              {renderedCellValue}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "itemName",
        header: "Tên Sản Phẩm",
        size: 400,
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "moTaKm",
        header: "Mô Tả Khuyến Mãi",
        size: 300,
        Cell: ({ renderedCellValue }) => (
          <Text size="sm" c={renderedCellValue ? "dark" : "dimmed"}>
            {renderedCellValue || "Chưa có mô tả"}
          </Text>
        ),
      },
      {
        accessorKey: "promotionDescriptionStatus",
        header: "Trạng Thái Duyệt",
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.promotionDescriptionStatus;
          const duyetMoTaKm = row.original.duyetMoTaKm;

          // Ưu tiên dùng duyetMoTaKm nếu có, fallback về promotionDescriptionStatus
          const isApproved =
            duyetMoTaKm === "1" || (duyetMoTaKm === null && status === true);

          return (
            <Badge
              color={isApproved ? "green" : "orange"}
              variant="light"
              size="sm"
            >
              {isApproved ? "Đã duyệt" : "Chưa duyệt"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "tenLinhVuc",
        header: "Lĩnh Vực",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "tenNganh",
        header: "Ngành",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "tenLoai",
        header: "Loại",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "tenChung",
        header: "Chủng",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "tenNhom",
        header: "Nhóm",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "tenHangSx",
        header: "Hãng Sản Xuất",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue || "N/A"}</>,
      },
      {
        accessorKey: "tenPl1",
        header: "Phân loại 1",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue || "N/A"}</>,
      },
      {
        accessorKey: "partNumber",
        header: "Part Number",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue || "N/A"}</>,
      },
      {
        accessorKey: "warranty",
        header: "Hạn BH (tháng)",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue || 0} tháng
          </Text>
        ),
      },
      {
        accessorKey: "warrantyVendor",
        header: "Hạn BH NCC (tháng)",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue || 0} tháng
          </Text>
        ),
      },
      {
        accessorKey: "isBuildPc",
        header: "Build PC",
        Cell: ({ renderedCellValue }) => (
          <Badge color={renderedCellValue === "Y" ? "green" : "gray"}>
            {renderedCellValue === "Y" ? "Có" : "Không"}
          </Badge>
        ),
      },
      {
        accessorKey: "lastUpdateDate",
        header: "Cập nhật cuối",
        Cell: ({ renderedCellValue }) => (
          <Text size="sm">
            {renderedCellValue && typeof renderedCellValue === "string"
              ? new Date(renderedCellValue).toLocaleDateString("vi-VN")
              : "N/A"}
          </Text>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <ActionIcon
            variant="light"
            color="blue"
            onClick={() => handleEditDescription(row.original)}
            title="Chỉnh sửa mô tả khuyến mãi"
          >
            <IconEdit size={16} />
          </ActionIcon>
        ),
      },
    ],
    [handleEditDescription]
  );

  const table = useMantineReactTable({
    columns,
    data: data || [],
    positionToolbarAlertBanner: "bottom",
    enableColumnResizing: true,
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["STT", "itemCode"],
        right: ["action"],
      },
      columnVisibility: { id: true },
      density: "xs",
    },
    enableRowSelection: true,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: true,
    rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontSize: "12.5px",
        padding: "5px 12px",
        fontWeight: 500,
      },
    }),
    state: {
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });

  useEffect(() => {
    fetchData();
  }, [pagination, fetchData]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (145 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} wrap={"wrap"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"} wrap={"wrap"}>
            <Button
              leftSection={
                <IconRefresh style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => fetchData()}
              color="green"
              variant="outline"
            >
              Làm mới
            </Button>

            <Button
              leftSection={
                <IconFileExport style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Button>
            <FileButton
              resetRef={resetRef}
              onChange={(e) => {
                if (e) {
                  if (
                    e &&
                    (e.type ===
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                      e.type === "application/vnd.ms-excel")
                  )
                    uploadFile(e);
                  else
                    NotificationExtension.Fails(
                      "File không đúng định đạng Excel !"
                    );
                }
              }}
            >
              {(props) => (
                <Button leftSection={<IconFileImport size={16} />} {...props}>
                  Import Excel
                </Button>
              )}
            </FileButton>
            <Button
              leftSection={
                <IconCheck style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => setBatchUpdateModalOpen(true)}
              disabled={Object.keys(rowSelection).length === 0}
            >
              Cập nhật trạng thái duyệt
            </Button>
            <Button
              leftSection={
                <IconCloudUpload style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => setSyncModalOpen(true)}
              disabled={Object.keys(rowSelection).length === 0}
            >
              Đồng bộ lên Web
            </Button>
          </Flex>
        </Flex>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <TextInput
              w={"100%"}
              placeholder="Từ khóa tìm kiếm"
              type="text"
              leftSection={<IconSearch color="#15aabf" />}
              defaultValue={search.keySearch}
              onChange={(e) =>
                handleChangeSearchValue(e.currentTarget.value, "keySearch")
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Lọc mô tả KM"
              data={[
                { value: "", label: "Tất cả" },
                { value: "true", label: "Có mô tả" },
                { value: "false", label: "Chưa có mô tả" },
              ]}
              value={search.hasPromotionDescription}
              onChange={(value) =>
                handleChangeSearchValue(value || "", "hasPromotionDescription")
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Lọc trạng thái duyệt"
              data={[
                { value: "", label: "Tất cả" },
                { value: "true", label: "Đã duyệt" },
                { value: "false", label: "Chưa duyệt" },
              ]}
              value={search.promotionDescriptionStatus}
              onChange={(value) =>
                handleChangeSearchValue(
                  value || "",
                  "promotionDescriptionStatus"
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
              color="blue"
              variant="outline"
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>

      <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>

      {/* Modal chỉnh sửa mô tả khuyến mãi */}
      <Modal
        opened={editModalOpen}
        onClose={handleCloseModal}
        title={`Chỉnh sửa mô tả khuyến mãi - ${editingItem?.itemCode}`}
        size="xl"
        centered
      >
        <div style={{ marginBottom: "10px" }}>
          <Text size="sm" fw={500} mb={5}>
            Sản phẩm: {editingItem?.itemName}
          </Text>
          <Text size="sm" fw={500} mb={10}>
            Mô tả khuyến mãi:
          </Text>
          <Editor
            apiKey="wwheiwaj492tbs9774t0z89v05r3xt8eg1ljilyc5d784xsv"
            value={promotionDescription}
            onEditorChange={(content: string) =>
              setPromotionDescription(content)
            }
            init={{
              height: 400,
              menubar: false,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "charmap",
                "preview",
                "searchreplace",
                "visualblocks",
                "code",
                "fullscreen",
                "insertdatetime",
                "table",
                "help",
                "wordcount",
              ],
              toolbar:
                "undo redo | blocks | " +
                "bold italic forecolor | alignleft aligncenter " +
                "alignright alignjustify | bullist numlist outdent indent | " +
                "removeformat | help",
              content_style:
                "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
              placeholder: "Nhập mô tả khuyến mãi cho sản phẩm...",
              branding: false,
            }}
          />
        </div>
        <Flex justify="flex-end" gap="md" mt="md">
          <Button variant="outline" onClick={handleCloseModal}>
            Hủy
          </Button>
          <Button
            loading={isUpdating}
            onClick={handleUpdateDescription}
            disabled={isContentEmpty(promotionDescription)}
          >
            Cập nhật
          </Button>
        </Flex>
      </Modal>

      {/* Modal cập nhật trạng thái duyệt hàng loạt */}
      <Modal
        opened={batchUpdateModalOpen}
        onClose={() => setBatchUpdateModalOpen(false)}
        title="Cập nhật trạng thái duyệt hàng loạt"
        centered
      >
        <Text size="sm" mb={10}>
          Bạn đã chọn <strong>{Object.keys(rowSelection).length}</strong> sản
          phẩm.
        </Text>
        <Text size="sm" fw={500} mb={10}>
          Chọn trạng thái duyệt:
        </Text>
        <Select
          data={[
            { value: "true", label: "Đã duyệt" },
            { value: "false", label: "Chưa duyệt" },
          ]}
          value={batchUpdateStatus.toString()}
          onChange={(value) => setBatchUpdateStatus(value === "true")}
          mb={20}
        />
        <Flex justify="flex-end" gap="md">
          <Button
            variant="outline"
            onClick={() => setBatchUpdateModalOpen(false)}
          >
            Hủy
          </Button>
          <Button loading={isUpdatingBatch} onClick={handleBatchUpdateStatus}>
            Cập nhật
          </Button>
        </Flex>
      </Modal>

      {/* Modal đồng bộ lên web */}
      <Modal
        opened={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        title="Đồng bộ sản phẩm lên Web"
        centered
      >
        <Text size="sm" mb={10}>
          Bạn đã chọn <strong>{Object.keys(rowSelection).length}</strong> sản
          phẩm.
        </Text>
        <Text size="sm" mb={20}>
          Hệ thống sẽ đồng bộ các sản phẩm có trạng thái mô tả khuyến mãi đang
          sử dụng lên website.
        </Text>
        <Flex justify="flex-end" gap="md">
          <Button variant="outline" onClick={() => setSyncModalOpen(false)}>
            Hủy
          </Button>
          <Button loading={isSyncing} onClick={handleSyncToWeb}>
            Đồng bộ
          </Button>
        </Flex>
      </Modal>
    </>
  );
};

export default DetailPromotion;
